// ====================================================================
// KIT — Alex's building/prop model kit (kit.glb)
// ====================================================================
// One meshopt-packed GLB holds every handmade building and prop model
// (houses, barn, closet, well, lantern, platforms) sharing a single texture
// atlas — see scripts/mergekit.mjs for how it's produced. This module loads
// it once at startup and hands out ready-to-render pieces:
//
//   - kitGeometry(kind): a model-local BufferGeometry, grounded pivot, with
//     gltfpack's dequantization transform baked in (the packed file parents
//     each mesh under an anonymous transform node — a naive
//     getObjectByName(kind).geometry would render at quantized scale).
//   - kitMaterial(): THE shared MeshLambertMaterial over the atlas — for
//     InstancedMesh prop types (chunks.js), where sharing is the point.
//   - kitMaterialClone(): a per-structure clone for buildings, because the
//     structure occlusion fade (main.js registerStructureFade) animates
//     material opacity — a shared material would ghost every building at
//     once. Clones share the one texture, so VRAM cost is nil.
//
// Loading NEVER blocks or breaks boot: loadKit() resolves false on any
// failure (timeout, offline first visit, corrupt file) and every consumer
// falls back to the procedural builders that predate the kit. Lambert (not
// the export's PBR material) because the whole game is Lambert: the export
// is effectively diffuse-only (metallic 0, black specular), and Lambert is
// what the Chromebook GPU budget is calibrated for.
//
// Core-safe: no DOM, no build flags; imported by main.js and chunks.js.
import * as THREE from 'three';
import { LOW } from '../core/tier.js';
import { makeGLTFLoader } from '../core/gltf.js';
import { loadGLBWithRetry } from '../core/net.js';

// Per-kind config. `fallback` {w,h,d} sizes the procedural stand-in when the
// kit is missing (baked worlds must never have holes); `inset` shrinks the
// collision AABB in XZ so Chocky can brush past eaves/fence rails that
// overhang the walls; `smoke` is the chimney-top offset (model-local) for
// kinds with a modeled chimney, null for the rest; `doubleSided` is the
// escape hatch for any model that shows FrontSide holes (none so far — the
// meshes are closed, and backface culling is cheap fill-rate savings).
export const KIT_KINDS = {
  house_small:    { fallback: { w: 3.1, h: 1.9, d: 2.6 }, inset: 0.10, smoke: null },
  house_big:      { fallback: { w: 4.1, h: 2.6, d: 2.6 }, inset: 0.10, smoke: { x: -1.35, y: 2.55, z: -0.55 } },
  log_cabin:      { fallback: { w: 2.7, h: 2.7, d: 3.1 }, inset: 0.28, smoke: { x: 0.75, y: 2.70, z: -0.55 } },
  numberzoo_barn: { fallback: { w: 3.5, h: 2.1, d: 2.6 }, inset: 0.10, smoke: null },
  chocky_closet:  { fallback: { w: 3.9, h: 2.0, d: 3.6 }, inset: 0.10, smoke: null },
  well:           { fallback: { w: 1.9, h: 2.0, d: 1.7 }, inset: 0.15, smoke: null },
  lantern:        { fallback: { w: 0.3, h: 1.9, d: 0.3 }, inset: 0,    smoke: null },
  platform_small: { fallback: { w: 0.9, h: 0.67, d: 0.85 }, inset: 0.06, smoke: null },
  platform_mid:   { fallback: { w: 0.9, h: 1.21, d: 0.85 }, inset: 0.06, smoke: null },
  platform_tall:  { fallback: { w: 0.9, h: 1.66, d: 0.85 }, inset: 0.06, smoke: null },
  // ---- assetpack2 additions. Structures (tower/castle) use inset for the
  // brush-past collision box; the props' inset is inert (they size their own
  // AABB from kitSize). Dims are the models' real world extents.
  wizard_tower:        { fallback: { w: 3.03, h: 6.38, d: 3.08 }, inset: 0.12, smoke: null },
  adventure101_castle: { fallback: { w: 3.03, h: 2.62, d: 2.99 }, inset: 0.12, smoke: null },
  platform_floating:   { fallback: { w: 0.91, h: 0.63, d: 0.91 }, inset: 0.06, smoke: null },
  bunny_hutch:         { fallback: { w: 0.85, h: 0.82, d: 0.55 }, inset: 0.05, smoke: null },
  haybale:             { fallback: { w: 0.72, h: 0.34, d: 0.35 }, inset: 0.04, smoke: null },
  maypole:             { fallback: { w: 0.93, h: 3.62, d: 0.93 }, inset: 0,    smoke: null },
};

// The kinds the editor's structure tool can place (buildings with identity;
// lantern/platforms are chunk props placed via the prop palette instead).
export const KIT_STRUCTURE_KINDS = ['house_small', 'house_big', 'log_cabin', 'numberzoo_barn', 'chocky_closet', 'well', 'wizard_tower', 'adventure101_castle'];

let _ready = false;
let _loadPromise = null;
const _geometries = new Map();   // kind -> BufferGeometry (model-local, grounded)
const _sizes = new Map();        // kind -> { w, h, d }
let _material = null;            // the shared Lambert over the atlas

export function kitReady() { return _ready; }

export function kitGeometry(kind) { return _geometries.get(kind) || null; }

export function kitSize(kind) { return _sizes.get(kind) || null; }

export function kitMaterial() { return _material; }

export function kitMaterialClone() { return _material ? _material.clone() : null; }

// Load + extract. Idempotent; resolves true/false, never rejects. The ~10s
// outer race mirrors bakedWorld.js: this gates the village build, and a
// stalled request on school Wi-Fi must not hang startup — resolve false and
// let the world build procedurally instead.
export function loadKit() {
  if (_loadPromise) return _loadPromise;
  _loadPromise = Promise.race([
    loadGLBWithRetry(makeGLTFLoader(), 'kit.glb', { retries: 2, timeoutMs: 8000 })
      .then((gltf) => extract(gltf)),
    new Promise((resolve) => setTimeout(() => resolve(false), 10000)),
  ]).catch((err) => {
    console.warn('[kit] kit.glb failed to load — using procedural fallbacks:', err && err.message || err);
    return false;
  });
  return _loadPromise;
}

// gltfpack ships quantized attributes (normalized Int16 positions / Int8
// normals) with the dequantization transform on the mesh's node. Baking that
// transform via applyMatrix4 would write world-space floats back into the
// normalized integer arrays — silently clamped garbage (a ~65,000-unit
// bounding box that floods the collision grid). Expand to plain Float32
// first; at ~2.2k tris for the whole kit the VRAM cost is noise.
function toFloat32Attribute(attr) {
  if (attr.array instanceof Float32Array && !attr.normalized) return attr;
  const out = new Float32Array(attr.count * attr.itemSize);
  const get = [attr.getX, attr.getY, attr.getZ, attr.getW];   // denormalizing getters
  for (let i = 0; i < attr.count; i++) {
    for (let j = 0; j < attr.itemSize; j++) out[i * attr.itemSize + j] = get[j].call(attr, i);
  }
  return new THREE.BufferAttribute(out, attr.itemSize);
}

function extract(gltf) {
  const root = gltf.scene;
  root.updateMatrixWorld(true);

  let atlas = null;
  for (const kind of Object.keys(KIT_KINDS)) {
    const named = root.getObjectByName(kind);
    if (!named) { console.warn(`[kit] kit.glb is missing "${kind}"`); continue; }
    let mesh = null;
    named.traverse((o) => { if (!mesh && o.isMesh) mesh = o; });
    if (!mesh) { console.warn(`[kit] "${kind}" has no mesh`); continue; }

    // Bake the packed file's dequantization transform into a clean copy, so
    // consumers get grounded, meter-scale, model-local geometry they can use
    // directly in an InstancedMesh or a structure group. Position/normal go
    // to Float32 first (see toFloat32Attribute); UVs stay quantized — they're
    // never written to, and the GPU reads them fine.
    const geo = mesh.geometry.clone();
    geo.setAttribute('position', toFloat32Attribute(geo.attributes.position));
    if (geo.attributes.normal) geo.setAttribute('normal', toFloat32Attribute(geo.attributes.normal));
    geo.applyMatrix4(mesh.matrixWorld);
    geo.computeBoundingBox();
    // Snap the pivot to the ground plane so every kind's local origin sits at its
    // base (this module's documented "grounded pivot" contract). A no-op for the
    // models authored grounded (min.y ≈ 0); it drops platform_floating's 0.12
    // pivot offset so the standable-AABB math (top = y + kitSize.h) stays exact.
    if (Math.abs(geo.boundingBox.min.y) > 1e-4) { geo.translate(0, -geo.boundingBox.min.y, 0); geo.computeBoundingBox(); }
    const bb = geo.boundingBox;
    _geometries.set(kind, geo);
    _sizes.set(kind, { w: bb.max.x - bb.min.x, h: bb.max.y - bb.min.y, d: bb.max.z - bb.min.z });

    if (!atlas && mesh.material && mesh.material.map) atlas = mesh.material.map;
  }

  if (!_geometries.size) return false;
  if (atlas) {
    atlas.colorSpace = THREE.SRGBColorSpace;
    // Anisotropic filtering keeps the kit atlas (house walls, lantern glass,
    // platform planks) crisp when seen edge-on rather than smearing at a
    // distance. three clamps the request to the GPU's real max at upload, so 16
    // is safe everywhere; off on the low tier where the atlas stays point-clean.
    atlas.anisotropy = LOW ? 1 : 16;
  }
  _material = new THREE.MeshLambertMaterial({ map: atlas });

  // The loader-built PBR materials are replaced wholesale — drop them (the
  // atlas texture itself is kept and shared by _material). The source geometries
  // are dropped too: consumers only ever use the Float32 clones in _geometries,
  // so releasing the originals' quantized typed arrays here frees memory sooner
  // on low-RAM Chromebooks (they were never GPU-uploaded — never rendered).
  root.traverse((o) => {
    if (!o.isMesh) return;
    if (o.material && o.material !== _material) o.material.dispose();
    if (o.geometry) o.geometry.dispose();
  });

  _ready = true;
  console.log(`[kit] loaded ${_geometries.size} models`);
  return true;
}
